/**
 * Horizontal bar chart showing domain distribution
 */

import React from 'react'

interface DomainChartProps {
  domains: { domain: string; count: number }[]
  onDomainClick?: (domain: string | null) => void
  selectedDomain?: string | null
  maxItems?: number
  totalCount?: number
}

export const DomainChart: React.FC<DomainChartProps> = ({
  domains,
  onDomainClick,
  selectedDomain,
  maxItems = 10,
  totalCount,
}) => {
  const displayDomains = domains.slice(0, maxItems)
  const total = totalCount || domains.reduce((sum, d) => sum + d.count, 0)
  const maxCount = total // Use total as max for relative sizing

  const formatDomain = (domain: string) => {
    return domain
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  // "All" is selected when selectedDomain is null
  const isAllSelected = selectedDomain === null

  return (
    <div className="space-y-0.5">
      {/* All option */}
      <button
        onClick={() => onDomainClick?.(null)}
        className={`w-full text-left group transition-all cursor-pointer rounded-md px-2 py-1 -mx-2 ${
          isAllSelected ? 'bg-accent/10' : 'hover:bg-tertiary/50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-tertiary rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all bg-accent group-hover:bg-accent/80"
              style={{ width: '100%' }}
            />
          </div>
          <div className="flex items-center justify-between w-28">
            <span
              className={`text-sm ${
                isAllSelected ? 'text-accent font-semibold' : 'text-primary'
              }`}
            >
              All
            </span>
            <span className="text-xs text-secondary tabular-nums">
              {total.toLocaleString()}
            </span>
          </div>
        </div>
      </button>

      {/* Domain bars */}
      {displayDomains.map(({ domain, count }) => {
        const percentage = (count / maxCount) * 100
        const isSelected = selectedDomain === domain

        return (
          <button
            key={domain}
            onClick={() => onDomainClick?.(domain)}
            className={`w-full text-left group transition-all cursor-pointer rounded-md px-2 py-1 -mx-2 ${
              isSelected ? 'bg-accent/10' : 'hover:bg-tertiary/50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-tertiary rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all bg-accent group-hover:bg-accent/80"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="flex items-center justify-between w-28">
                <span
                  className={`text-sm truncate ${
                    isSelected ? 'text-accent font-semibold' : 'text-primary'
                  }`}
                >
                  {formatDomain(domain)}
                </span>
                <span className="text-xs text-secondary tabular-nums">
                  {count.toLocaleString()}
                </span>
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default DomainChart
